import { useEffect, useRef, useCallback } from 'react';

export interface UsePollingOptions<T> {
  fetchFn: () => Promise<T>;
  isComplete: (data: T) => boolean;
  onData: (data: T) => void;
  onError?: (err: unknown) => void;
  intervalMs?: number;
  maxAttempts?: number;
  enabled?: boolean;
}

/**
 * Generic polling hook. Calls `fetchFn` repeatedly at `intervalMs` intervals
 * until `isComplete` returns true, `enabled` becomes false, or `maxAttempts`
 * is reached. Cleans up on unmount and restarts when options identity changes.
 */
export function usePolling<T>(options: UsePollingOptions<T>): { stop: () => void } {
  const {
    fetchFn,
    isComplete,
    onData,
    onError,
    intervalMs = 1000,
    maxAttempts = 3600,
    enabled = false,
  } = options;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);
  const activeRef = useRef(false);

  // Stable refs for callbacks so the polling loop always sees the latest
  const fetchFnRef = useRef(fetchFn);
  const isCompleteRef = useRef(isComplete);
  const onDataRef = useRef(onData);
  const onErrorRef = useRef(onError);

  fetchFnRef.current = fetchFn;
  isCompleteRef.current = isComplete;
  onDataRef.current = onData;
  onErrorRef.current = onError;

  const clearPoll = useCallback(() => {
    activeRef.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Always clean up previous poll cycle when deps change
    clearPoll();

    if (!enabled) return;

    activeRef.current = true;
    attemptsRef.current = 0;

    const poll = async () => {
      if (!activeRef.current) return;
      if (attemptsRef.current >= maxAttempts) return;
      attemptsRef.current++;

      try {
        const data = await fetchFnRef.current();
        if (!activeRef.current) return;

        onDataRef.current(data);

        if (isCompleteRef.current(data)) {
          activeRef.current = false;
          return;
        }
      } catch (err) {
        if (!activeRef.current) return;
        onErrorRef.current?.(err);
        return;
      }

      if (activeRef.current) {
        timeoutRef.current = setTimeout(poll, intervalMs);
      }
    };

    poll();

    return clearPoll;
  }, [enabled, intervalMs, maxAttempts, clearPoll]);

  return { stop: clearPoll };
}
